import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { Card } from '@/console/features/card-layout/components/card/card';
import { TEmptyCard } from '@/console/features/card-layout/components/card/card.types';
import { ProjectCardRow } from '@/console/features/card-layout/components/recent-projects-card/components/project-card-row/project-card-row';
import { LatestConversionStatusBatchProvider } from '@/console/features/projects/components/conversion-status-batch/conversion-status-batch-context';
import { ProjectDrawer } from '@/console/features/projects/components/project-drawer/project-drawer';
import { AddIcon } from '@/shared/assets/icons/add-icon';
import { ProjectsIcon } from '@/shared/assets/icons/projects-icon';
import { ShowIcon } from '@/shared/assets/icons/show-icon';
import { MenuItem } from '@/shared/components/ui/menu-item/menu-item';
import { ProjectsFilterBuilder } from '@/shared/filters/entity-filters/project-filter-builder/project-filter-builder';
import { useDirectFilter } from '@/shared/filters/hooks/use-direct-filter/use-direct-filter';
import { useBackgroundRefetch } from '@/shared/hooks/use-background-refetch/use-background-refetch';
import {
  projectsApi,
  useReadAllProjectsQuery,
} from '@/shared/redux/api/assets/projects/projects-api';
import {
  ProjectFragment,
  ProjectTypeEnum,
} from '@/shared/types/types.generated';

export const RecentProjectsCard = () => {
  const { t } = useTranslation();
  const filter = useDirectFilter(new ProjectsFilterBuilder(), {
    projectType: ProjectTypeEnum.Standard,
  });
  const prefetchProjects = projectsApi.usePrefetch('ReadAllProjects');
  const { data, isLoading, isFetching } = useReadAllProjectsQuery({
    filter,
    pagination: {
      page: 1,
      pageSize: 15,
    },
  });
  const visibleIsFetching = useBackgroundRefetch(isFetching);
  const showLoader = isLoading || visibleIsFetching;
  const projects = data?.readAllProjects?.items;
  const navigate = useNavigate();

  const versionIds = useMemo(
    () =>
      projects
        ?.map((p) => p.versions?.items?.[0]?.versionId)
        .filter((id): id is string => !!id) ?? [],
    [projects],
  );

  const projectIds = useMemo(
    () => projects?.map((p) => p.projectId) ?? [],
    [projects],
  );

  const renderPopoverContent = () => {
    return (
      <>
        <MenuItem onClick={() => navigate('/projects')} icon={<ShowIcon />}>
          {t('card_layout:view_all_projects')}
        </MenuItem>
        <ProjectDrawer component="MenuItem" icon={<AddIcon />} disableTabs />
      </>
    );
  };

  const getEmptyCardProps = (): TEmptyCard => {
    return {
      action: (
        <ProjectDrawer component="Button" icon={<AddIcon />} disableTabs />
      ),
      icon: <ProjectsIcon />,
      text: t('card_layout:projects.empty_message'),
    };
  };

  return (
    <LatestConversionStatusBatchProvider
      projectIds={projectIds}
      versionIds={versionIds}
    >
      <Card<ProjectFragment>
        onMouseEnter={() => prefetchProjects()}
        title={t('card_layout:projects.title')}
        popoverContent={renderPopoverContent()}
        emptyCard={getEmptyCardProps()}
        showLoader={showLoader}
        data={projects}
        row={(project) => (
          <ProjectCardRow project={project} key={project.projectId} />
        )}
      />
    </LatestConversionStatusBatchProvider>
  );
};
